import React, { useState, useEffect } from 'react';
import axios from 'axios';

const PDFReports = () => {
  const [forms, setForms] = useState([]);
  const [students, setStudents] = useState([]);
  const [schoolLunchSubmissions, setSchoolLunchSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Selection state
  const [selectedForm, setSelectedForm] = useState('');
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectedSubmissions, setSelectedSubmissions] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Filtering state
  const [statusFilter, setStatusFilter] = useState('all');
  const [gradeFilter, setGradeFilter] = useState('all');

  // PDF generation state
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [pdfData, setPdfData] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load forms first
      const formsResponse = await axios.get('/api/reports/pdf/forms');
      setForms(formsResponse.data);

      // Don't load students initially - wait for form selection

    } catch (error) {
      console.error('Error loading PDF report data:', error);
      setError('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  // Load students for the selected form
  const loadStudentsForForm = async (formId) => {
    try {
      let url = '/api/reports/pdf/students';
      if (formId && formId !== 'school-lunch-application') {
        url += `?form_instance_id=${formId}`;
      }
      const studentsResponse = await axios.get(url);
      setStudents(studentsResponse.data);
    } catch (error) {
      console.error('Error loading students for form:', error);
      setError('Failed to load students for selected form');
      setStudents([]);
    }
  };

  // Load school lunch submissions when school lunch form is selected
  const loadSchoolLunchSubmissions = async () => {
    try {
      const response = await axios.get('/api/forms/school-lunch-application/submissions');
      setSchoolLunchSubmissions(response.data);
    } catch (error) {
      console.error('Error loading school lunch submissions:', error);
      setError('Failed to load school lunch submissions');
    }
  };

  // Get unique statuses and grades for filtering
  const uniqueStatuses = [...new Set(students.map(s => s.status))];
  const uniqueGrades = [...new Set(students.map(s => s.grade).filter(g => g))].sort();

  // Filter students based on current filters
  const filteredStudents = students.filter(student => {
    const statusMatch = statusFilter === 'all' || student.status === statusFilter;
    const gradeMatch = gradeFilter === 'all' || student.grade === gradeFilter;
    return statusMatch && gradeMatch;
  });

  // Check if selected form is school lunch
  const isSchoolLunchForm = selectedForm === 'school-lunch-application';

  // Handle select all checkbox
  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (isSchoolLunchForm) {
      if (checked) {
        setSelectedSubmissions(schoolLunchSubmissions.map(s => s.id));
      } else {
        setSelectedSubmissions([]);
      }
    } else {
      if (checked) {
        // TEMPORARILY select all filtered students for debugging
        setSelectedStudents(filteredStudents.map(s => s.id));
      } else {
        setSelectedStudents([]);
      }
    }
  };

  // Handle individual student selection
  const handleStudentSelect = (studentId, checked) => {
    if (checked) {
      setSelectedStudents(prev => [...prev, studentId]);
    } else {
      setSelectedStudents(prev => prev.filter(id => id !== studentId));
      setSelectAll(false);
    }
  };

  // Handle individual submission selection
  const handleSubmissionSelect = (submissionId, checked) => {
    if (checked) {
      setSelectedSubmissions(prev => [...prev, submissionId]);
    } else {
      setSelectedSubmissions(prev => prev.filter(id => id !== submissionId));
      setSelectAll(false);
    }
  };

  // Update select all state when filtered items change
  useEffect(() => {
    if (isSchoolLunchForm) {
      if (schoolLunchSubmissions.length > 0) {
        const allSelected = schoolLunchSubmissions.every(submission => selectedSubmissions.includes(submission.id));
        const someSelected = schoolLunchSubmissions.some(submission => selectedSubmissions.includes(submission.id));
        setSelectAll(allSelected && someSelected);
      }
    } else {
      if (filteredStudents.length > 0) {
        // Only consider selectable students for select all state
        const selectableStudents = filteredStudents.filter(student =>
          selectedForm && selectedForm !== 'school-lunch-application' ? student.has_completed_form : true
        );

        if (selectableStudents.length > 0) {
          const allSelected = selectableStudents.every(student => selectedStudents.includes(student.id));
          const someSelected = selectableStudents.some(student => selectedStudents.includes(student.id));
          setSelectAll(allSelected && someSelected);
        } else {
          setSelectAll(false);
        }
      }
    }
  }, [filteredStudents, selectedStudents, schoolLunchSubmissions, selectedSubmissions, isSchoolLunchForm, selectedForm]);

  // Handle form selection
  const handleFormSelection = async (formId) => {
    setSelectedForm(formId);
    setSelectedStudents([]);
    setSelectedSubmissions([]);
    setSelectAll(false);
    setError('');

    if (formId === 'school-lunch-application') {
      await loadSchoolLunchSubmissions();
    } else if (formId) {
      await loadStudentsForForm(formId);
    } else {
      // No form selected, clear students
      setStudents([]);
    }
  };

  // Generate PDF report
  const handleGeneratePDF = async () => {
    const selectedItems = isSchoolLunchForm ? selectedSubmissions : selectedStudents;

    if (!selectedForm || selectedItems.length === 0) {
      setError(`Please select a form and at least one ${isSchoolLunchForm ? 'family' : 'student'}`);
      return;
    }

    // Clear any previous PDF data and errors
    setPdfData(null);
    setError('');

    try {
      setGeneratingPDF(true);
      setError('');

      let response;
      if (isSchoolLunchForm) {
        // Handle school lunch form generation
        if (selectedSubmissions.length === 1) {
          response = await axios.post('/api/forms/school-lunch-application/generate-pdf', {
            submissionId: selectedSubmissions[0]
          }, {
            responseType: 'blob'
          });
        } else {
          // For multiple submissions, we'd need a batch endpoint - for now just generate the first one
          response = await axios.post('/api/forms/school-lunch-application/generate-pdf', {
            submissionId: selectedSubmissions[0]
          }, {
            responseType: 'blob'
          });
        }
      } else {
        response = await axios.post('/api/reports/pdf/generate', {
          form_instance_id: selectedForm,
          student_ids: selectedStudents
        }, {
          responseType: selectedStudents.length === 1 ? 'blob' : 'json'
        });
      }

      if ((isSchoolLunchForm && selectedSubmissions.length === 1) || (!isSchoolLunchForm && selectedStudents.length === 1)) {
        // Single PDF - download directly
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${isSchoolLunchForm ? 'school_lunch' : 'form_report'}_${Date.now()}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        setPdfData({ message: 'PDF downloaded successfully!' });
      } else if (isSchoolLunchForm) {
        // School lunch - only single family supported for now
        setPdfData({ message: 'Currently only single school lunch application PDFs can be generated. Please select one family.' });
        } else {
          // Multiple PDFs - create ZIP file and download
          const pdfData = response.data;
          console.log(`Received response with ${pdfData.pdfs ? pdfData.pdfs.length : 'undefined'} PDFs for ZIP creation`);
          console.log('PDF data structure:', pdfData);

          if (!pdfData.pdfs || pdfData.pdfs.length === 0) {
            console.error('No PDFs received from server');
            setError('No PDFs were generated. Please check the server logs.');
            return;
          }

          // Load JSZip from CDN if not already loaded
          if (!window.JSZip) {
            console.log('Loading JSZip library...');
            const script = document.createElement('script');
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js';
            script.onload = () => createAndDownloadZip(pdfData);
            script.onerror = () => {
              console.error('Failed to load JSZip library');
              setError('Failed to load ZIP library. Please try again.');
            };
            document.head.appendChild(script);
          } else {
            createAndDownloadZip(pdfData);
          }

          function createAndDownloadZip(pdfData) {
            try {
              console.log('Creating ZIP file...');
              const zip = new window.JSZip();
              let validPdfs = 0;

              // Add each PDF to the ZIP
              pdfData.pdfs.forEach((pdf, index) => {
                try {
                  console.log(`Adding PDF ${index + 1} to ZIP: ${pdf.filename}, data length: ${pdf.data.length}`);

                  // Validate base64 format
                  const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
                  if (!base64Regex.test(pdf.data)) {
                    console.error(`Invalid base64 format for ${pdf.filename}, skipping`);
                    return;
                  }

                  // Convert base64 to Uint8Array for JSZip
                  const binaryString = atob(pdf.data);
                  const bytes = new Uint8Array(binaryString.length);
                  for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                  }

                  // Add to ZIP
                  zip.file(pdf.filename, bytes);
                  validPdfs++;
                  console.log(`Successfully added ${pdf.filename} to ZIP`);

                } catch (error) {
                  console.error(`Failed to add ${pdf.filename} to ZIP:`, error);
                }
              });

              if (validPdfs === 0) {
                setError('No valid PDFs to include in ZIP file.');
                return;
              }

              // Generate ZIP file
              console.log(`Generating ZIP file with ${validPdfs} PDFs`);
              zip.generateAsync({ type: 'blob' }).then((zipBlob) => {
                console.log(`ZIP file generated, size: ${zipBlob.size} bytes`);

                // Download the ZIP file
                const zipUrl = window.URL.createObjectURL(zipBlob);
                const link = document.createElement('a');
                const timestamp = new Date().toISOString().split('T')[0];
                link.href = zipUrl;
                link.download = `pdf_reports_${timestamp}.zip`;
                link.style.display = 'none';

                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(zipUrl);

                console.log('ZIP file download initiated');

                setPdfData({
                  record_count: pdfData.pdfs.length,
                  message: `Successfully created ZIP file with ${validPdfs} PDF${validPdfs > 1 ? 's' : ''}. Check your downloads folder.`,
                  downloaded: true
                });
              }).catch((error) => {
                console.error('Failed to generate ZIP file:', error);
                setError('Failed to create ZIP file. Please try again.');
              });

            } catch (error) {
              console.error('Failed to create ZIP:', error);
              setError('Failed to create ZIP file. Please try again.');
            }
          }
      }

    } catch (error) {
      console.error('Error generating PDF:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      setError(error.response?.data?.error || error.message || 'Failed to generate PDF');
    } finally {
      setGeneratingPDF(false);
    }
  };

  // Print the report
  const handlePrint = () => {
    if (!pdfData) return;

    // Create a printable version of the data
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>PDF Report Summary</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #2c3e50; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            .success { color: #27ae60; font-weight: bold; }
          </style>
        </head>
        <body>
          <h1>PDF Report Summary</h1>
          <p>Report generated on ${new Date().toLocaleString()}</p>
          <p class="success">${pdfData.message}</p>
          <p><strong>Total PDFs Generated:</strong> ${pdfData.record_count}</p>

          <h3>Generated PDF Files:</h3>
          <table>
            <thead>
              <tr>
                <th>Student Name</th>
                <th>Filename</th>
              </tr>
            </thead>
            <tbody>
    `);

    // Add file information
    if (pdfData.files) {
      pdfData.files.forEach((file) => {
        printWindow.document.write(`
          <tr>
            <td>${file.studentName}</td>
            <td>${file.filename}</td>
          </tr>
        `);
      });
    }

    printWindow.document.write(`
            </tbody>
          </table>

          <p><em>Note: Individual PDF files have been generated and downloaded to your device.</em></p>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading PDF Reports...</div>;
  }

  return (
    <div style={{ padding: '1rem', maxWidth: '1200px', margin: '0 auto' }}>
      <h2>PDF Reports</h2>

      {error && (
        <div style={{
          background: '#f8d7da',
          color: '#721c24',
          padding: '0.75rem',
          borderRadius: '4px',
          marginBottom: '1rem',
          border: '1px solid #f5c6cb'
        }}>
          {error}
        </div>
      )}

      {/* Form Selection */}
      <div style={{ marginBottom: '2rem' }}>
        <h3>Select Form</h3>
        <select
          value={selectedForm}
          onChange={(e) => handleFormSelection(e.target.value)}
          style={{
            padding: '0.5rem',
            border: '1px solid #ddd',
            borderRadius: '4px',
            minWidth: '300px'
          }}
        >
          <option value="">Choose a form...</option>
          {forms.map(form => (
            <option key={form.id} value={form.id}>
              {form.instance_name || form.form_name}
            </option>
          ))}
          <option value="school-lunch-application">School Lunch Application</option>
        </select>
      </div>

      {/* Student/Family Selection */}
      <div style={{ marginBottom: '2rem' }}>
        <h3>{isSchoolLunchForm ? 'Select Families' : 'Select Students'}</h3>

        {/* Filters - only show for regular forms, not school lunch */}
        {!isSchoolLunchForm && (
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <div>
              <label style={{ marginRight: '0.5rem' }}>Status:</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{ padding: '0.25rem', border: '1px solid #ddd', borderRadius: '4px' }}
              >
                <option value="all">All Statuses</option>
                {uniqueStatuses.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ marginRight: '0.5rem' }}>Grade:</label>
              <select
                value={gradeFilter}
                onChange={(e) => setGradeFilter(e.target.value)}
                style={{ padding: '0.25rem', border: '1px solid #ddd', borderRadius: '4px' }}
              >
                <option value="all">All Grades</option>
                {uniqueGrades.map(grade => (
                  <option key={grade} value={grade}>{grade}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Student Table */}
        <div style={{
          border: '1px solid #ddd',
          borderRadius: '4px',
          maxHeight: '400px',
          overflowY: 'auto'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{
              background: '#f8f9fa',
              position: 'sticky',
              top: 0,
              zIndex: 1
            }}>
              <tr>
                <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </th>
                <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                  {isSchoolLunchForm ? 'Parent Name' : 'Student Name'}
                </th>
                {isSchoolLunchForm ? (
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                    Students
                  </th>
                ) : (
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                    Grade
                  </th>
                )}
                <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                  {isSchoolLunchForm ? 'Submitted Date' : 'Status'}
                </th>
              </tr>
            </thead>
            <tbody>
              {isSchoolLunchForm ? (
                // School lunch submissions
                schoolLunchSubmissions.map(submission => (
                  <tr key={submission.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '0.75rem' }}>
                      <input
                        type="checkbox"
                        checked={selectedSubmissions.includes(submission.id)}
                        onChange={(e) => handleSubmissionSelect(submission.id, e.target.checked)}
                      />
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      {submission.parent_first_name} {submission.parent_last_name}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      {submission.students && submission.students.length > 0
                        ? submission.students.map(s => `${s.first_name} ${s.last_name}`).join(', ')
                        : 'No students listed'}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      {new Date(submission.submitted_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))
              ) : (
                // Regular students
                filteredStudents.map(student => {
                  const canSelect = selectedForm && selectedForm !== 'school-lunch-application' ? student.has_completed_form : true;
                  return (
                    <tr key={student.id} style={{
                      borderBottom: '1px solid #eee',
                      opacity: canSelect ? 1 : 0.5,
                      backgroundColor: canSelect ? 'transparent' : '#f8f9fa'
                    }}>
                      <td style={{ padding: '0.75rem' }}>
                        <input
                          type="checkbox"
                          checked={selectedStudents.includes(student.id)}
                          onChange={(e) => handleStudentSelect(student.id, e.target.checked)}
                          disabled={false}
                          title={!canSelect ? 'Student has not completed this form' : ''}
                        />
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        {student.first_name} {student.last_name}
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        {student.grade || 'N/A'}
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        {student.status}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: '1rem' }}>
          {isSchoolLunchForm ? (
            <div style={{ color: '#666' }}>
              Showing {schoolLunchSubmissions.length} families
              {selectedSubmissions.length > 0 && ` (${selectedSubmissions.length} selected)`}
            </div>
          ) : selectedForm && selectedForm !== 'school-lunch-application' ? (
            <div>
              <div style={{ color: '#666' }}>
                Showing {filteredStudents.length} students
                {selectedStudents.length > 0 && ` (${selectedStudents.length} selected)`}
              </div>
              <div style={{ color: '#6c757d', fontSize: '0.9em', marginTop: '0.25rem' }}>
                Grayed out students have not completed this form and cannot be selected.
              </div>
            </div>
          ) : (
            <div style={{ color: '#666' }}>
              Showing {filteredStudents.length} students
              {selectedStudents.length > 0 && ` (${selectedStudents.length} selected)`}
            </div>
          )}
        </div>
      </div>

      {/* Generate PDF Button */}
      <div style={{ marginBottom: '2rem' }}>
        <button
          onClick={handleGeneratePDF}
          disabled={!selectedForm || (isSchoolLunchForm ? selectedSubmissions.length === 0 : selectedStudents.length === 0) || generatingPDF}
          style={{
            background: (!selectedForm || (isSchoolLunchForm ? selectedSubmissions.length === 0 : selectedStudents.length === 0) || generatingPDF) ? '#bdc3c7' : '#27ae60',
            color: 'white',
            border: 'none',
            padding: '0.75rem 2rem',
            borderRadius: '4px',
            cursor: (!selectedForm || (isSchoolLunchForm ? selectedSubmissions.length === 0 : selectedStudents.length === 0) || generatingPDF) ? 'not-allowed' : 'pointer',
            fontSize: '1rem',
            fontWeight: '600'
          }}
        >
          {generatingPDF ? 'Generating PDF...' : 'Run Report'}
        </button>
      </div>

      {/* PDF Results */}
      {pdfData && pdfData.record_count && (
        <div style={{
          border: '1px solid #ddd',
          borderRadius: '4px',
          padding: '1rem',
          background: '#f8f9fa'
        }}>
          <h3>Report Generated Successfully</h3>
          <p>{pdfData.message}</p>

          {/* Manual download links if available */}
          {pdfData.pdfs && pdfData.pdfs.length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              <h4>Manual Downloads:</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <button
                  onClick={() => {
                    // Load JSZip if not loaded
                    if (!window.JSZip) {
                      const script = document.createElement('script');
                      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js';
                      script.onload = () => createManualZip(pdfData.pdfs);
                      script.onerror = () => alert('Failed to load ZIP library');
                      document.head.appendChild(script);
                    } else {
                      createManualZip(pdfData.pdfs);
                    }

                    function createManualZip(pdfs) {
                      try {
                        const zip = new window.JSZip();
                        let validCount = 0;

                        pdfs.forEach((pdf) => {
                          try {
                            const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
                            if (!base64Regex.test(pdf.data)) return;

                            const binaryString = atob(pdf.data);
                            const bytes = new Uint8Array(binaryString.length);
                            for (let i = 0; i < binaryString.length; i++) {
                              bytes[i] = binaryString.charCodeAt(i);
                            }

                            zip.file(pdf.filename, bytes);
                            validCount++;
                          } catch (e) {
                            console.error(`Failed to add ${pdf.filename} to ZIP:`, e);
                          }
                        });

                        if (validCount === 0) {
                          alert('No valid PDFs to download');
                          return;
                        }

                        zip.generateAsync({ type: 'blob' }).then((zipBlob) => {
                          const zipUrl = window.URL.createObjectURL(zipBlob);
                          const link = document.createElement('a');
                          const timestamp = new Date().toISOString().split('T')[0];
                          link.href = zipUrl;
                          link.download = `pdf_reports_manual_${timestamp}.zip`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          window.URL.revokeObjectURL(zipUrl);
                        });
                      } catch (error) {
                        alert(`Failed to create ZIP: ${error.message}`);
                      }
                    }
                  }}
                  style={{
                    background: '#28a745',
                    color: 'white',
                    border: 'none',
                    padding: '0.5rem 1rem',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontSize: '1rem',
                    fontWeight: 'bold'
                  }}
                >
                  📦 Download All as ZIP ({pdfData.pdfs.length} files)
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PDFReports;


